import type {
  AcceptChallengeResponse,
  ActiveGamesResponse,
  AdminStatsResponse,
  AdminUsersResponse,
  BotLevel,
  ChallengesResponse,
  ChangePasswordRequest,
  ClientEventType,
  CreateBotRoomResponse,
  CreateChallengeResponse,
  FavoritesResponse,
  GameDetailResponse,
  GameHistoryResponse,
  InviteCodeResponse,
  LeaderboardResponse,
  Me,
  NicknameAvailableResponse,
  OpponentsResponse,
  PlayersResponse,
  ProfileStatsResponse,
  RegisterRequest,
  RematchResponse,
  ResetPasswordResponse,
  TrackEventRequest,
} from '@sea-wars/protocol';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

/**
 * Wszystkie żądania idą z ciasteczkiem sesji (`credentials: 'include'`).
 * Klient nie przechowuje żadnego tokenu — sesja jest w ciasteczku `httpOnly`,
 * niedostępnym dla skryptu, więc nie ma tu czego trzymać.
 */
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    headers: init.body ? { 'content-type': 'application/json' } : {},
    ...init,
  });

  if (response.status === 204) return undefined as T;

  const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    throw new ApiError(
      response.status,
      typeof body.code === 'string' ? body.code : 'UNKNOWN',
      typeof body.message === 'string' ? body.message : 'Coś poszło nie tak',
    );
  }
  return body as T;
}

export const api = {
  me: () => request<Me>('/api/me'),
  login: (login: string, password: string) =>
    request<Me>('/api/auth/login', { method: 'POST', body: JSON.stringify({ login, password }) }),
  register: (payload: RegisterRequest) =>
    request<Me>('/api/auth/register', { method: 'POST', body: JSON.stringify(payload) }),
  nicknameAvailable: (nickname: string) =>
    request<NicknameAvailableResponse>(
      `/api/auth/nickname-available?nickname=${encodeURIComponent(nickname)}`,
    ),
  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),
  changePassword: (payload: ChangePasswordRequest) =>
    request<void>('/api/me/password', { method: 'POST', body: JSON.stringify(payload) }),
  inviteCode: () => request<InviteCodeResponse>('/api/auth/invite-code'),
  createBotRoom: (level: BotLevel) =>
    request<CreateBotRoomResponse>('/api/rooms/bot', {
      method: 'POST',
      body: JSON.stringify({ level }),
    }),
  history: (params: { cursor?: string; limit?: number } = {}) => {
    const query = new URLSearchParams();
    if (params.cursor) query.set('cursor', params.cursor);
    if (params.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return request<GameHistoryResponse>(`/api/games${qs ? `?${qs}` : ''}`);
  },
  gameDetail: (id: string) => request<GameDetailResponse>(`/api/games/${id}`),
  activeGames: () => request<ActiveGamesResponse>('/api/me/active-games'),
  stats: () => request<ProfileStatsResponse>('/api/me/stats'),
  leaderboard: () => request<LeaderboardResponse>('/api/leaderboard'),
  adminStats: () => request<AdminStatsResponse>('/api/admin/stats'),
  adminUsers: () => request<AdminUsersResponse>('/api/admin/users'),
  setUserStatus: (id: string, status: 'active' | 'blocked') =>
    request<void>(`/api/admin/users/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    }),
  setUserAdmin: (id: string, isAdmin: boolean) =>
    request<void>(`/api/admin/users/${id}/admin`, {
      method: 'POST',
      body: JSON.stringify({ isAdmin }),
    }),
  deleteUser: (id: string) => request<void>(`/api/admin/users/${id}/delete`, { method: 'POST' }),
  resetUserPassword: (id: string) =>
    request<ResetPasswordResponse>(`/api/admin/users/${id}/reset-password`, { method: 'POST' }),
  opponents: () => request<OpponentsResponse>('/api/me/opponents'),
  favorites: () => request<FavoritesResponse>('/api/me/favorites'),
  players: (query = '') =>
    request<PlayersResponse>(`/api/players${query ? `?q=${encodeURIComponent(query)}` : ''}`),
  addFavorite: (userId: string) => request<void>(`/api/me/favorites/${userId}`, { method: 'POST' }),
  removeFavorite: (userId: string) =>
    request<void>(`/api/me/favorites/${userId}`, { method: 'DELETE' }),
  createChallenge: (targetUserId: string) =>
    request<CreateChallengeResponse>('/api/challenges', {
      method: 'POST',
      body: JSON.stringify({ targetUserId }),
    }),
  challenges: () => request<ChallengesResponse>('/api/challenges'),
  acceptChallenge: (id: string) =>
    request<AcceptChallengeResponse>(`/api/challenges/${id}/accept`, { method: 'POST' }),
  declineChallenge: (id: string) =>
    request<void>(`/api/challenges/${id}/decline`, { method: 'POST' }),
  cancelChallenge: (id: string) =>
    request<void>(`/api/challenges/${id}/cancel`, { method: 'POST' }),
  requestRematch: (gameId: string) =>
    request<RematchResponse>(`/api/games/${gameId}/rematch`, { method: 'POST' }),
  trackEvent: (
    type: ClientEventType,
    options: { gameId?: string; props?: TrackEventRequest['props'] } = {},
  ) =>
    request<void>('/api/events', {
      method: 'POST',
      body: JSON.stringify({ type, ...options }),
    }),
};
